/**
 * Converte un array di oggetti in formato CSV
 *
 * Usa il separatore di campo `;` e la virgola come separatore decimale, in modo che
 * Excel in locale italiano apra il file direttamente in colonne senza perdere i decimali
 * (con `,` come separatore campo e `.` come decimale, Excel interpreta "1234.000" come
 * numero e rimuove gli zeri/il punto in fase di apertura).
 *
 * @param data Array di oggetti da convertire
 * @param headers Array di header personalizzati (opzionale). Se è presente `type: 'number'`
 *   i valori numerici di quella colonna vengono formattati con la virgola come decimale.
 * @returns Stringa CSV
 */
export function convertToCSV(
  data: readonly any[] | any[],
  headers?: { field: string; headerName: string; type?: string }[]
): string {
  if (data.length === 0) {
    return '';
  }

  // Se non ci sono header personalizzati, usa le chiavi del primo oggetto
  const keys = headers
    ? headers.map(h => h.field)
    : Object.keys(data[0]).filter(key => !key.startsWith('_')); // Escludi chiavi interne

  // Crea la riga degli header
  const headerNames = headers
    ? headers.map(h => h.headerName)
    : keys;

  const fieldTypeMap = new Map<string, string | undefined>(
    headers ? headers.map(h => [h.field, h.type]) : []
  );

  const csvHeaders = headerNames.map(header => escapeCSVValue(header)).join(';');

  // Crea le righe dei dati
  const csvRows = data.map(row => {
    return keys
      .map(key => {
        const value = row[key];
        // Gestisci valori null/undefined
        if (value === null || value === undefined) {
          return '';
        }
        // Gestisci oggetti e array
        if (typeof value === 'object') {
          return escapeCSVValue(JSON.stringify(value));
        }
        const numeric = toCSVNumericString(value, fieldTypeMap.get(key));
        if (numeric !== null) {
          return escapeCSVValue(numeric);
        }
        return escapeCSVValue(String(value));
      })
      .join(';');
  });

  return [csvHeaders, ...csvRows].join('\n');
}

const PLAIN_DECIMAL_STRING = /^[+-]?\d+(\.\d+)?$/;
const EXPONENT_DECIMAL_STRING = /^[+-]?\d+(\.\d+)?[eE][+-]?\d+$/;

/**
 * Formatta un numero per il CSV usando la virgola come separatore decimale
 * (convenzione italiana, coerente con il separatore di campo `;`).
 * Non usa mai la notazione scientifica (1e-7) e non inserisce separatori delle migliaia.
 */
export function formatCSVNumber(value: number): string {
  if (!Number.isFinite(value)) {
    return String(value);
  }
  const plain = Number.isInteger(value)
    ? String(value).includes('e')
      ? value.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 0 })
      : String(value)
    : value.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 });
  return plain.replace('.', ',');
}

/**
 * Se il valore e' numerico restituisce la stringa CSV con virgola decimale, altrimenti `null`.
 *
 * Gestisce sia i `number` sia le stringhe numeriche con punto decimale (es. i `Decimal`
 * serializzati dal backend come "1234.5600"): senza conversione Excel in locale italiano
 * interpreta il punto come separatore delle migliaia e il valore diventa 12345600.
 * Le colonne esplicitamente di tipo diverso da `number` (es. `string`) non vengono toccate se
 * il valore e' una stringa.
 */
export function toCSVNumericString(value: unknown, fieldType?: string): string | null {
  if (typeof value === 'number') {
    return formatCSVNumber(value);
  }
  if (typeof value === 'string' && (fieldType === 'number' || fieldType === undefined)) {
    const trimmed = value.trim();
    if (PLAIN_DECIMAL_STRING.test(trimmed) && trimmed.includes('.')) {
      return trimmed.replace('.', ',');
    }
    if (EXPONENT_DECIMAL_STRING.test(trimmed)) {
      return formatCSVNumber(Number(trimmed));
    }
  }
  return null;
}

/**
 * Escapa un valore per CSV (gestisce virgolette, punto e virgola, newline)
 */
function escapeCSVValue(value: string): string {
  if (value.includes(';') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Scarica un file CSV
 * @param csvContent Contenuto CSV come stringa
 * @param filename Nome del file (senza estensione)
 */
export function downloadCSV(csvContent: string, filename: string): void {
  // BOM UTF-8 per far riconoscere a Excel la codifica ed evitare caratteri accentati corrotti
  const blob = new Blob(['\uFEFF', csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}
